/**
 * Envío de eventos a Google Analytics 4 (gtag).
 *
 * `gtag` se carga en `public/index.html`. Este módulo NO envía `page_view`: la medición
 * mejorada de GA4 ya los registra en cada cambio de ruta (History API); duplicarlos
 * inflaría las vistas del informe.
 *
 * Todas las funciones son no-op si gtag no está disponible (dev sin script, bloqueadores
 * de anuncios, tests): nunca deben romper la vista que las llama.
 */

/** @returns {boolean} true si gtag está cargado y utilizable. */
function gtagReady() {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

/**
 * Envía un evento a GA4. Los parámetros `undefined`/`null` se descartan.
 * @param {string} name Nombre del evento (snake_case, sin espacios).
 * @param {Record<string, string|number|boolean|null|undefined>} [params]
 */
export function trackEvent(name, params = {}) {
  if (!gtagReady() || !name) return;
  const clean = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') clean[k] = v;
  }
  try {
    window.gtag('event', name, clean);
  } catch {
    /* la analítica nunca debe interrumpir la navegación */
  }
}

/** Recorrido elegido en la portada. @param {string} recorridoId */
export const trackSeleccionRecorrido = (recorridoId) =>
  trackEvent('seleccionar_recorrido', { recorrido_id: recorridoId });

/**
 * Apertura de la vista recorrido (mapa + ETA).
 * @param {{recorridoId: string, paraderoId?: string|null}} p
 */
export const trackVerParadero = ({ recorridoId, paraderoId }) =>
  trackEvent('ver_paradero', { recorrido_id: recorridoId, paradero_id: paraderoId });

/**
 * Cambio de sentido (ida / regreso) dentro de la vista recorrido.
 * @param {{recorridoId: string, sentido: string}} p
 */
export const trackCambioSentido = ({ recorridoId, sentido }) =>
  trackEvent('cambiar_sentido', { recorrido_id: recorridoId, sentido });

/** Apertura del itinerario. @param {{recorridoId: string, sentido?: string|null}} p */
export const trackVerItinerario = ({ recorridoId, sentido }) =>
  trackEvent('ver_itinerario', { recorrido_id: recorridoId, sentido });

/**
 * Búsqueda de paraderos cercanos por geolocalización. No se envía la ubicación, solo el resultado.
 * @param {'ok' | 'denegado' | 'error' | 'no_disponible'} resultado
 */
export const trackBuscarCercanos = (resultado) => trackEvent('buscar_paraderos_cercanos', { resultado });

/**
 * Apertura del tablero de llegadas de un paradero.
 * @param {{paraderoFisicoId: string, origen?: 'cercania' | 'busqueda' | null}} p
 */
export const trackVerLlegadas = ({ paraderoFisicoId, origen }) =>
  trackEvent('ver_llegadas', { paradero_fisico_id: paraderoFisicoId, origen });
